import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner, EmptyState } from "@/components/opl/Common";

export function useList(path) {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get(path).then((r) => setData(r.data)).catch((e) => { toast.error(errMsg(e)); setData([]); }), [path]);
  useEffect(() => { load(); }, [load]);
  return [data, load];
}

export function ImageUpload({ value, onChange, folder, testid = "image-upload" }) {
  const [busy, setBusy] = useState(false);
  const up = async (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      const r = await api.post(`/uploads/image?folder=${folder}`, fd);
      onChange(r.data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-3">
      {value ? <img src={fileUrl(value)} alt="" className="h-14 w-14 rounded-lg object-cover border border-white/10" /> : <div className="h-14 w-14 rounded-lg bg-white/5" />}
      <label className="cursor-pointer flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-white/10 hover:border-[#E5B842]/50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
        <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid={testid} onChange={(e) => up(e.target.files[0])} />
      </label>
    </div>
  );
}

export function Field({ f, value, onChange }) {
  const id = `field-${f.name}`;
  let input;
  if (f.type === "select")
    input = (
      <Select value={value ?? ""} onValueChange={onChange}>
        <SelectTrigger data-testid={id}><SelectValue placeholder={f.placeholder || "Select"} /></SelectTrigger>
        <SelectContent>{f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    );
  else if (f.type === "switch") input = <Switch checked={!!value} onCheckedChange={onChange} data-testid={id} />;
  else if (f.type === "textarea") input = <Textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={id} />;
  else if (f.type === "image") input = <ImageUpload value={value} onChange={onChange} folder={f.folder} testid={id} />;
  else
    input = <Input type={f.type || "text"} value={value ?? ""} required={f.required} step="any" placeholder={f.placeholder}
      onChange={(e) => onChange(f.type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)} data-testid={id} />;
  return (
    <div className={`space-y-1.5 ${f.full ? "sm:col-span-2" : ""}`}>
      <Label htmlFor={id}>{f.label}{f.required && <span className="text-[#E5B842]"> *</span>}</Label>
      {input}
      {f.hint && <p className="text-[11px] text-slate-500">{f.hint}</p>}
    </div>
  );
}

export function FormDialog({ open, onOpenChange, title, fields, initial, onSubmit, testid = "form-dialog" }) {
  const [v, setV] = useState(initial || {});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setV(initial || {}); }, [initial, open]);
  const submit = async (e) => {
    e.preventDefault();
    for (const f of fields) if (f.required && (v[f.name] === undefined || v[f.name] === "" || v[f.name] === null)) return toast.error(`${f.label} is required`);
    setBusy(true);
    try {
      await onSubmit(v);
      onOpenChange(false);
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0B1323] border-[#E5B842]/20 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={testid}>
        <DialogHeader><DialogTitle className="font-display text-xl">{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
          {fields.filter((f) => !f.hidden?.(v)).map((f) => <Field key={f.name} f={f} value={v[f.name]} onChange={(x) => setV((s) => ({ ...s, [f.name]: x }))} />)}
          <DialogFooter className="sm:col-span-2 mt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-full border border-white/10 text-sm" data-testid="form-cancel-btn">Cancel</button>
            <button disabled={busy} className="px-5 py-2 rounded-full gold-bg font-bold text-sm flex items-center gap-2" data-testid="form-submit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save</button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function DataTable({ rows, columns, testid = "data-table", empty = "No records", rowKey = "id" }) {
  if (rows === null) return <Spinner />;
  if (!rows.length) return <EmptyState title={empty} />;
  return (
    <div className="surface rounded-xl overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/5 text-left text-[11px] uppercase tracking-widest text-slate-400">
            {columns.map((c) => <th key={c.key} className="px-4 py-3 font-semibold whitespace-nowrap">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r[rowKey]} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors" data-testid={`row-${r.code || r[rowKey]}`}>
              {columns.map((c) => <td key={c.key} className="px-4 py-3 align-middle">{c.render ? c.render(r) : r[c.key] ?? "—"}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const GoldBtn = ({ children, testid, ...p }) => (
  <button data-testid={testid} {...p} className={`flex items-center gap-2 px-4 py-2 rounded-full gold-bg font-bold text-sm hover:brightness-110 transition-[filter] disabled:opacity-40 ${p.className || ""}`}>{children}</button>
);
export const GhostBtn = ({ children, testid, ...p }) => (
  <button data-testid={testid} {...p} className={`flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-sm hover:border-[#E5B842]/50 transition-colors disabled:opacity-40 ${p.className || ""}`}>{children}</button>
);
export const IconBtn = ({ children, testid, title, ...p }) => (
  <button data-testid={testid} title={title} {...p} className={`p-2 rounded-md text-slate-400 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-30 ${p.className || ""}`}>{children}</button>
);
